import { Optional } from "@/types/util"
import isEmpty from "lodash/isEmpty"

// nullish + empty string, empty array, empty object
export type CollectionTypes =
  | string
  | Array<unknown>
  | Record<string, unknown>
  | Set<unknown>
  | Map<unknown, unknown>
  | object

// Returns true iff the given value is null or undefined and
// narrows to a nullable type.
export function nullish<T>(value: Optional<T>): boolean {
  return value == null
}

export function empty<T extends CollectionTypes>(value: Optional<T>): boolean {
  return isEmpty(value)
}

// empty + blank string + false
export function blank<T>(value: Optional<T>): boolean {
  return (
    value === false ||
    nullish(value) ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && empty(value)) ||
    (value instanceof Set && empty(value)) ||
    (value instanceof Map && empty(value)) ||
    // null and undefined captured already
    (typeof value === "object" && empty(value))
  )
}

// opposite of blank
export function present<T extends NonNullable<unknown>>(
  value: Optional<T>
): value is T {
  return !blank(value)
}

// opposite of empty
export function some<T extends CollectionTypes>(
  value: Optional<T>
): value is T {
  return !empty(value)
}

// Opposite of nullish. Returns true iff the value is not null and
// not undefined, AND it narrows to a non-nullish type.
export function exists<T extends NonNullable<unknown>>(
  value: Optional<T>
): value is T {
  return !nullish(value)
}
